import { Combobox } from '@ordo/ui/combobox'
import { DatePicker, LocalDateTimePicker } from '@ordo/ui/date-picker'
import { Input } from '@ordo/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@ordo/ui/select'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { RRule, Weekday } from 'rrule'
import { cn } from 'utils/cn'

export interface RRuleOptions {
  freq: number
  interval: number
  byweekday: number[]
  bymonthday: number[]
  dtstart: string
  hour: number
  minute: number
  endType: 'never' | 'count' | 'until'
  count: number
  until: string
}

const FREQ_OPTIONS = [
  { value: RRule.DAILY, label: 'Daily' },
  { value: RRule.WEEKLY, label: 'Weekly' },
  { value: RRule.MONTHLY, label: 'Monthly' },
  { value: RRule.YEARLY, label: 'Yearly' },
]

const WEEKDAYS = [
  { value: RRule.MO.weekday, label: 'Mo' },
  { value: RRule.TU.weekday, label: 'Tu' },
  { value: RRule.WE.weekday, label: 'We' },
  { value: RRule.TH.weekday, label: 'Th' },
  { value: RRule.FR.weekday, label: 'Fr' },
  { value: RRule.SA.weekday, label: 'Sa' },
  { value: RRule.SU.weekday, label: 'Su' },
]

const TIMEZONE_OPTIONS = Intl.supportedValuesOf('timeZone').map((tz) => ({ value: tz, label: tz.replace(/_/g, ' ') }))

function getLocalTimezone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone
}

/** Convert a date string + hour/minute in a given timezone to a UTC Date. */
function localToUtc(dateStr: string, hour: number, minute: number, timezone: string): Date {
  // Build an ISO-like string and parse it as if it were in the target timezone
  // by using Intl to find the UTC offset at that local time
  const padH = String(hour).padStart(2, '0')
  const padM = String(minute).padStart(2, '0')
  // Create a rough UTC guess, then correct for the timezone offset
  const guess = new Date(`${dateStr}T${padH}:${padM}:00Z`)
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  })
  // Find what the guess time looks like in the target timezone
  const parts = formatter.formatToParts(guess)
  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? 0)
  const localAtGuess = new Date(
    Date.UTC(
      get('year'),
      get('month') - 1,
      get('day'),
      get('hour') === 24 ? 0 : get('hour'),
      get('minute'),
      get('second'),
    ),
  )
  // The offset is the difference between what we wanted locally and what we got
  const offsetMs = localAtGuess.getTime() - guess.getTime()
  return new Date(guess.getTime() - offsetMs)
}

/** Convert a UTC Date to components in a given timezone. */
function utcToLocal(utcDate: Date, timezone: string): { date: string; hour: number; minute: number } {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })
  const parts = formatter.formatToParts(utcDate)
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '0'
  const y = get('year')
  const m = get('month')
  const d = get('day')
  const h = Number(get('hour'))
  return {
    date: `${y}-${m}-${d}`,
    hour: h === 24 ? 0 : h,
    minute: Number(get('minute')),
  }
}

function defaultOptions(): RRuleOptions {
  const now = new Date()
  return {
    freq: RRule.WEEKLY,
    interval: 1,
    byweekday: [RRule.MO.weekday],
    bymonthday: [1],
    dtstart: now.toISOString().slice(0, 10),
    hour: 9,
    minute: 0,
    endType: 'never',
    count: 10,
    until: new Date(now.getFullYear() + 1, now.getMonth(), now.getDate()).toISOString().slice(0, 10),
  }
}

function optionsToRRule(opts: RRuleOptions, timezone: string): string {
  // User enters time in their selected timezone — convert to UTC for the rrule
  const dtstart = localToUtc(opts.dtstart, opts.hour, opts.minute, timezone)

  const ruleOpts: Partial<InstanceType<typeof RRule>['options']> = {
    freq: opts.freq,
    interval: opts.interval,
    dtstart,
  }

  if (opts.freq === RRule.WEEKLY && opts.byweekday.length > 0) {
    ruleOpts.byweekday = opts.byweekday
  }

  if (opts.freq === RRule.MONTHLY && opts.bymonthday.length > 0) {
    ruleOpts.bymonthday = opts.bymonthday
  }

  if (opts.endType === 'count') {
    ruleOpts.count = opts.count
  } else if (opts.endType === 'until') {
    ruleOpts.until = new Date(`${opts.until}T23:59:59Z`)
  }

  const rule = new RRule(ruleOpts)
  return rule.toString()
}

function parseRRule(rruleStr: string): RRuleOptions | null {
  try {
    const rule = RRule.fromString(rruleStr)
    const opts = rule.origOptions
    const dtstart = opts.dtstart ?? new Date()
    const result = defaultOptions()

    result.freq = opts.freq ?? RRule.WEEKLY
    result.interval = opts.interval ?? 1
    result.dtstart = dtstart.toISOString().slice(0, 10)
    result.hour = dtstart.getUTCHours()
    result.minute = dtstart.getUTCMinutes()

    if (opts.byweekday) {
      const days = Array.isArray(opts.byweekday) ? opts.byweekday : [opts.byweekday]
      result.byweekday = days.map((d) => {
        if (typeof d === 'number') {
          return d
        }
        if (d instanceof Weekday) {
          return d.weekday
        }
        const weekday = WEEKDAYS.find((w) => w.label === d)!
        return weekday.value
      })
    }

    if (opts.bymonthday) {
      const days = Array.isArray(opts.bymonthday) ? opts.bymonthday : [opts.bymonthday]
      result.bymonthday = days
    }

    if (opts.count) {
      result.endType = 'count'
      result.count = opts.count
    } else if (opts.until) {
      result.endType = 'until'
      result.until = opts.until.toISOString().slice(0, 10)
    } else {
      result.endType = 'never'
    }

    return result
  } catch {
    return null
  }
}

const INTERVAL_LABELS: Record<number, string> = {
  [RRule.DAILY]: 'day(s)',
  [RRule.WEEKLY]: 'week(s)',
  [RRule.MONTHLY]: 'month(s)',
  [RRule.YEARLY]: 'year(s)',
}

/** Format a short timezone abbreviation like "EDT" or "PST" for display. */
function tzAbbr(timezone: string, date: Date): string {
  const parts = new Intl.DateTimeFormat('en-US', { timeZone: timezone, timeZoneName: 'short' }).formatToParts(date)
  return parts.find((p) => p.type === 'timeZoneName')?.value ?? timezone
}

export function RRuleBuilder({ value, onChange }: { value: string; onChange: (rrule: string) => void }) {
  const [timezone, setTimezone] = useState(getLocalTimezone)
  const [options, setOptions] = useState<RRuleOptions>(() => {
    if (value) {
      const parsed = parseRRule(value)
      if (parsed) {
        // The stored rrule has UTC times — convert to the user's local timezone for display
        const local = utcToLocal(localToUtc(parsed.dtstart, parsed.hour, parsed.minute, 'UTC'), getLocalTimezone())
        return { ...parsed, dtstart: local.date, hour: local.hour, minute: local.minute }
      }
    }
    return defaultOptions()
  })

  // Emit the default rrule on mount when parent starts with empty value
  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange
  const [emittedInitial, setEmittedInitial] = useState(false)
  useEffect(() => {
    if (!emittedInitial && !value) {
      onChangeRef.current(optionsToRRule(options, timezone))
      setEmittedInitial(true)
    }
  }, [emittedInitial, value, options, timezone])

  // Sync external value changes (e.g. loading existing schedule)
  useEffect(() => {
    if (value) {
      const parsed = parseRRule(value)
      if (parsed) {
        const local = utcToLocal(localToUtc(parsed.dtstart, parsed.hour, parsed.minute, 'UTC'), timezone)
        setOptions({ ...parsed, dtstart: local.date, hour: local.hour, minute: local.minute })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value])

  const handleTimezoneChange = useCallback(
    (tz: string) => {
      // Convert current local time from old timezone to new timezone
      const utcDate = localToUtc(options.dtstart, options.hour, options.minute, timezone)
      const local = utcToLocal(utcDate, tz)
      setTimezone(tz)
      setOptions((prev) => {
        const next = { ...prev, dtstart: local.date, hour: local.hour, minute: local.minute }
        queueMicrotask(() => onChange(optionsToRRule(next, tz)))
        return next
      })
    },
    [options.dtstart, options.hour, options.minute, timezone, onChange],
  )

  const updateOption = useCallback(
    <K extends keyof RRuleOptions>(key: K, val: RRuleOptions[K]) => {
      setOptions((prev) => {
        const next = { ...prev, [key]: val }
        const rrule = optionsToRRule(next, timezone)
        // Defer onChange to avoid updating parent during render
        queueMicrotask(() => onChange(rrule))
        return next
      })
    },
    [onChange, timezone],
  )

  const toggleWeekday = useCallback(
    (day: number) => {
      setOptions((prev) => {
        const current = prev.byweekday
        const next = current.includes(day) ? current.filter((d) => d !== day) : [...current, day].sort()
        // Ensure at least one day selected
        if (next.length === 0) {
          return prev
        }
        const updated = { ...prev, byweekday: next }
        queueMicrotask(() => onChange(optionsToRRule(updated, timezone)))
        return updated
      })
    },
    [onChange, timezone],
  )

  const toggleMonthday = useCallback(
    (day: number) => {
      setOptions((prev) => {
        const current = prev.bymonthday
        const next = current.includes(day) ? current.filter((d) => d !== day) : [...current, day].sort((a, b) => a - b)
        if (next.length === 0) {
          return prev
        }
        const updated = { ...prev, bymonthday: next }
        queueMicrotask(() => onChange(optionsToRRule(updated, timezone)))
        return updated
      })
    },
    [onChange, timezone],
  )

  const preview = useMemo(() => {
    try {
      const rule = RRule.fromString(optionsToRRule(options, timezone))
      return rule.toText()
    } catch {
      return ''
    }
  }, [options, timezone])

  const nextDates = useMemo(() => {
    try {
      const rule = RRule.fromString(optionsToRRule(options, timezone))
      return rule.all((_, i) => i < 3)
    } catch {
      return []
    }
  }, [options, timezone])

  const abbr = useMemo(() => tzAbbr(timezone, new Date()), [timezone])

  return (
    <div className="flex flex-col gap-4">
      {/* Frequency + Interval */}
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm text-muted-foreground">Every</span>
        <Input
          type="number"
          min={1}
          max={99}
          value={options.interval}
          onChange={(e) => updateOption('interval', Math.max(1, Number(e.target.value)))}
          className="h-8 w-16 text-center text-sm"
        />
        <Select value={String(options.freq)} onValueChange={(v) => updateOption('freq', Number(v))}>
          <SelectTrigger className="h-8 w-auto gap-1 text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {FREQ_OPTIONS.map((f) => (
              <SelectItem key={f.value} value={String(f.value)}>
                {INTERVAL_LABELS[f.value]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Weekly: day-of-week */}
      {options.freq === RRule.WEEKLY && (
        <div className="space-y-1.5">
          <span className="text-sm font-medium text-muted-foreground">On days</span>
          <div className="flex gap-1">
            {WEEKDAYS.map((day) => (
              <button
                key={day.value}
                type="button"
                onClick={() => toggleWeekday(day.value)}
                className={cn(
                  'flex h-8 w-9 items-center justify-center rounded-md text-xs font-medium transition-colors',
                  options.byweekday.includes(day.value)
                    ? 'bg-primary text-primary-foreground'
                    : 'border border-input bg-background text-foreground hover:bg-muted',
                )}>
                {day.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Monthly: day-of-month */}
      {options.freq === RRule.MONTHLY && (
        <div className="space-y-1.5">
          <span className="text-sm font-medium text-muted-foreground">On day(s) of month</span>
          <div className="flex flex-wrap gap-1">
            {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
              <button
                key={day}
                type="button"
                onClick={() => toggleMonthday(day)}
                className={cn(
                  'flex h-7 w-7 items-center justify-center rounded text-xs font-medium transition-colors',
                  options.bymonthday.includes(day)
                    ? 'bg-primary text-primary-foreground'
                    : 'border border-input bg-background text-foreground hover:bg-muted',
                )}>
                {day}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* First occurrence date + time */}
      <div className="space-y-1.5">
        <span className="text-sm font-medium text-muted-foreground">First Occurrence</span>
        <div>
          <LocalDateTimePicker
            date={options.dtstart}
            hour={options.hour}
            minute={options.minute}
            onDateChange={(v) => updateOption('dtstart', v)}
            onHourChange={(v) => updateOption('hour', v)}
            onMinuteChange={(v) => updateOption('minute', v)}
            timezoneLabel={abbr}
          />
        </div>
      </div>

      {/* Timezone */}
      <div className="space-y-1.5">
        <span className="text-sm font-medium text-muted-foreground">Timezone</span>
        <Combobox
          options={TIMEZONE_OPTIONS}
          value={timezone}
          onValueChange={handleTimezoneChange}
          placeholder="Select timezone..."
          searchPlaceholder="Search timezones..."
          className="h-8 text-sm"
        />
      </div>

      {/* End condition */}
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm text-muted-foreground">Ends</span>
        <Select value={options.endType} onValueChange={(v) => updateOption('endType', v as RRuleOptions['endType'])}>
          <SelectTrigger className="h-8 w-auto gap-1 text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="never">Never</SelectItem>
            <SelectItem value="count">After</SelectItem>
            <SelectItem value="until">On date</SelectItem>
          </SelectContent>
        </Select>
        {options.endType === 'count' && (
          <>
            <Input
              type="number"
              min={1}
              max={999}
              value={options.count}
              onChange={(e) => updateOption('count', Math.max(1, Number(e.target.value)))}
              className="h-8 w-20 text-center text-sm"
            />
            <span className="text-sm text-muted-foreground">occurrences</span>
          </>
        )}
        {options.endType === 'until' && <DatePicker value={options.until} onChange={(v) => updateOption('until', v)} />}
      </div>

      {/* Human-readable preview + next dates */}
      {preview && (
        <div className="rounded-md bg-muted/30 px-3 py-2">
          <p className="text-sm capitalize text-foreground">{preview}</p>
          {nextDates.length > 0 && (
            <div className="mt-1.5 space-y-0.5">
              <p className="text-xs font-medium text-muted-foreground">Next occurrences:</p>
              {nextDates.map((d, i) => (
                <p key={i} className="text-xs text-muted-foreground">
                  {d.toLocaleString(undefined, { timeZone: timezone })} {abbr}
                </p>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
